import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

// Mobile bottom tab bar (shown ≤768px; hidden on desktop where the top AppNav handles nav).
// Home / Insights / Account are direct links; Money / Markets mirror the desktop dropdown
// groups by opening an upward sheet with their sub-pages.

const MONEY = [['Budgets', '/budgets'], ['Expenses', '/expenses'], ['Goals', '/goals']];
const MARKETS = [['Automations', '/automations'], ['Portfolio', '/portfolio'], ['Watchlist', '/watchlist']];

const svg = (children) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {children}
  </svg>
);

const ICONS = {
  home: svg(<><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><path d="M9 22V12h6v10" /></>),
  insights: svg(<><path d="M9 18h6" /><path d="M10 22h4" /><path d="M12 2a7 7 0 0 0-4 12.7c.6.5 1 1.3 1 2.3h6c0-1 .4-1.8 1-2.3A7 7 0 0 0 12 2z" /></>),
  money: svg(<><path d="M19 7V5a2 2 0 0 0-2-2H5a2 2 0 0 0 0 4h15a1 1 0 0 1 1 1v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5" /><path d="M16 12h.01" /></>),
  markets: svg(<><polyline points="23 6 13.5 15.5 8.5 10.5 1 18" /><polyline points="17 6 23 6 23 12" /></>),
  account: svg(<><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></>),
};

export default function BottomNav() {
  const { pathname } = useLocation();
  const [sheet, setSheet] = useState(null); // 'money' | 'markets' | null

  // Pad the page bottom + lift the chat FAB only while the bar is mounted (authed).
  useEffect(() => {
    document.body.classList.add('has-bottom-nav');
    return () => document.body.classList.remove('has-bottom-nav');
  }, []);

  // Close the sheet whenever the route changes.
  useEffect(() => { setSheet(null); }, [pathname]);

  // Close on Escape, matching the existing backdrop-click dismissal.
  useEffect(() => {
    if (!sheet) return undefined;
    function onKeyDown(e) { if (e.key === 'Escape') setSheet(null); }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [sheet]);

  const inGroup = (items) => items.some(([, to]) => pathname === to || pathname.startsWith(`${to}/`));
  const moneyActive = inGroup(MONEY) || sheet === 'money';
  const marketsActive = inGroup(MARKETS) || sheet === 'markets';
  const sheetItems = sheet === 'money' ? MONEY : sheet === 'markets' ? MARKETS : [];

  return (
    <>
      {sheet && <div className="bn-backdrop" data-testid="bn-backdrop" onClick={() => setSheet(null)} aria-hidden="true" />}
      {sheet && (
        <div className="bn-sheet" role="menu" aria-label={sheet === 'money' ? 'Money' : 'Markets'}>
          {sheetItems.map(([label, to]) => (
            <Link key={to} to={to} className="bn-sheet-link" role="menuitem" onClick={() => setSheet(null)}>{label}</Link>
          ))}
        </div>
      )}
      <nav className="bottom-nav" aria-label="Primary">
        <Link to="/" className={`bn-tab${pathname === '/' ? ' bn-tab--active' : ''}`} aria-current={pathname === '/' ? 'page' : undefined} onClick={() => setSheet(null)}>
          {ICONS.home}<span>Home</span>
        </Link>
        <Link to="/insights" className={`bn-tab${pathname === '/insights' ? ' bn-tab--active' : ''}`} aria-current={pathname === '/insights' ? 'page' : undefined} onClick={() => setSheet(null)}>
          {ICONS.insights}<span>Insights</span>
        </Link>
        <button type="button" className={`bn-tab${moneyActive ? ' bn-tab--active' : ''}`} aria-haspopup="menu" aria-expanded={sheet === 'money'} onClick={() => setSheet((s) => (s === 'money' ? null : 'money'))}>
          {ICONS.money}<span>Money</span>
        </button>
        <button type="button" className={`bn-tab${marketsActive ? ' bn-tab--active' : ''}`} aria-haspopup="menu" aria-expanded={sheet === 'markets'} onClick={() => setSheet((s) => (s === 'markets' ? null : 'markets'))}>
          {ICONS.markets}<span>Markets</span>
        </button>
        <Link to="/settings" className={`bn-tab${pathname === '/settings' ? ' bn-tab--active' : ''}`} aria-current={pathname === '/settings' ? 'page' : undefined} onClick={() => setSheet(null)}>
          {ICONS.account}<span>Account</span>
        </Link>
      </nav>
    </>
  );
}
